import { isClock, isIsoDate } from './calendar';

/** Editable text values of a deadline form (empty string = not set). */
export interface DeadlineDraft {
  hasStart: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  /** Events only: becomes overdue after its end when not completed (default off). */
  overdueEnabled?: boolean;
}

export type DeadlineErrorCode =
  | 'endRequired'
  | 'endDateInvalid'
  | 'endTimeInvalid'
  | 'startRequired'
  | 'startDateInvalid'
  | 'startTimeInvalid'
  | 'endBeforeStart'
  | 'startTimeRequired'
  | 'endTimeRequired'
  | 'endNotAfterStart';

export interface DeadlinePayload {
  date: string;
  time: string | null;
  start_date: string | null;
  start_time: string | null;
  overdue_enabled?: boolean;
}

/** Same rule as the backend: a date-only start begins at 00:00, a date-only end ends at 24:00. */
function stamp(date: string, time: string, endOfDay: boolean): string {
  return `${date}T${time || (endOfDay ? '24:00' : '00:00')}`;
}

/**
 * Client-side validation (the server validates again). Returns the first problem per field
 * group so the form can mark the right control.
 */
export function validateDraft(draft: DeadlineDraft): DeadlineErrorCode[] {
  const errors: DeadlineErrorCode[] = [];
  if (!draft.endDate) errors.push('endRequired');
  else if (!isIsoDate(draft.endDate)) errors.push('endDateInvalid');
  if (draft.endTime && !isClock(draft.endTime)) errors.push('endTimeInvalid');
  if (draft.hasStart) {
    if (!draft.startDate) errors.push('startRequired');
    else if (!isIsoDate(draft.startDate)) errors.push('startDateInvalid');
    if (draft.startTime && !isClock(draft.startTime)) errors.push('startTimeInvalid');
  }
  if (errors.length > 0) return errors;
  if (
    draft.hasStart &&
    stamp(draft.endDate, draft.endTime, true) < stamp(draft.startDate, draft.startTime, false)
  ) {
    errors.push('endBeforeStart');
  }
  return errors;
}

/**
 * Validation of a NEW EVENT (an explicit start and end): both endpoints are required and the end
 * must come later than the start. A timed event needs both times; an all-day event is dates
 * only (the end date may equal the start date). Tasks (deadline only) use {@link validateDraft}.
 */
export function validateEventDraft(draft: DeadlineDraft, allDay: boolean): DeadlineErrorCode[] {
  const errors: DeadlineErrorCode[] = [];
  if (!draft.startDate) errors.push('startRequired');
  else if (!isIsoDate(draft.startDate)) errors.push('startDateInvalid');
  if (!draft.endDate) errors.push('endRequired');
  else if (!isIsoDate(draft.endDate)) errors.push('endDateInvalid');
  if (!allDay) {
    if (!draft.startTime) errors.push('startTimeRequired');
    else if (!isClock(draft.startTime)) errors.push('startTimeInvalid');
    if (!draft.endTime) errors.push('endTimeRequired');
    else if (!isClock(draft.endTime)) errors.push('endTimeInvalid');
  }
  if (errors.length > 0) return errors;
  if (allDay) {
    if (draft.endDate < draft.startDate) errors.push('endBeforeStart');
  } else if (`${draft.endDate}T${draft.endTime}` <= `${draft.startDate}T${draft.startTime}`) {
    errors.push('endNotAfterStart');
  }
  return errors;
}

export function draftToPayload(draft: DeadlineDraft): DeadlinePayload {
  return {
    date: draft.endDate,
    time: draft.endTime || null,
    start_date: draft.hasStart ? draft.startDate : null,
    start_time: draft.hasStart ? draft.startTime || null : null,
    // Only an event has the setting; a task never sends it.
    ...(draft.hasStart && { overdue_enabled: draft.overdueEnabled === true }),
  };
}

interface DeadlineLike {
  date: string;
  time: string | null;
  start_date: string | null;
  start_time: string | null;
  overdue_enabled?: boolean;
}

/** `HH:MM:SS` from the server -> `HH:MM`. */
const clock = (time: string | null) => (time ? time.slice(0, 5) : '');

export function draftFromDeadline(deadline: DeadlineLike | null): DeadlineDraft {
  return {
    hasStart: deadline?.start_date != null,
    startDate: deadline?.start_date ?? '',
    startTime: clock(deadline?.start_time ?? null),
    endDate: deadline?.date ?? '',
    endTime: clock(deadline?.time ?? null),
    overdueEnabled: deadline?.overdue_enabled === true,
  };
}

/**
 * The approved rules for the date editor (UI/UX 2.0), one function so every screen agrees:
 *
 * | start | end   | result                             |
 * |-------|-------|------------------------------------|
 * | empty | empty | `none`  - a task without a date    |
 * | empty | set   | `task`  - a deadline               |
 * | set   | set   | `event` - a period                 |
 * | set   | empty | `invalid` (the end is required)    |
 *
 * A time without its date counts as that endpoint being half filled.
 */
export type RangeKind = 'none' | 'task' | 'event' | 'invalid';

export function rangeKind(draft: DeadlineDraft): RangeKind {
  const start = draft.startDate !== '' || draft.startTime !== '';
  const end = draft.endDate !== '' || draft.endTime !== '';
  if (!start && !end) return 'none';
  if (start && !end) return 'invalid';
  if (!start) return 'task';
  return 'event';
}

/** Problems of the draft as it stands (empty list = it may be saved). */
export function validateRange(draft: DeadlineDraft): DeadlineErrorCode[] {
  const kind = rangeKind(draft);
  if (kind === 'none') return [];
  const errors: DeadlineErrorCode[] = [];
  const hasStart = draft.startDate !== '' || draft.startTime !== '';
  if (hasStart && !draft.startDate) errors.push('startRequired');
  if (!draft.endDate) errors.push('endRequired');
  if (errors.length > 0) return errors;
  return validateDraft({ ...draft, hasStart });
}

/** The payload to save, or `null` for "no deadline". Call only for a valid draft. */
export function rangeToPayload(draft: DeadlineDraft): DeadlinePayload | null {
  if (rangeKind(draft) === 'none') return null;
  return draftToPayload({ ...draft, hasStart: draft.startDate !== '' });
}

/** How long an event lasts: whole days for an all-day one (both ends inclusive), else minutes. */
export type DraftDuration = { kind: 'days'; days: number } | { kind: 'minutes'; minutes: number };

const dayNumber = (date: string): number => {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
};

const minuteOf = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * The calculated length of a draft that has both endpoints, or `null` when it cannot be told
 * (a missing or invalid endpoint, or an end that does not come after the start). Date-only edges
 * follow the backend: a date-only start begins at 00:00, a date-only end finishes with its day.
 */
export function draftDuration(draft: DeadlineDraft): DraftDuration | null {
  if (!isIsoDate(draft.startDate) || !isIsoDate(draft.endDate)) return null;
  if (draft.startTime && !isClock(draft.startTime)) return null;
  if (draft.endTime && !isClock(draft.endTime)) return null;
  const first = dayNumber(draft.startDate);
  const last = dayNumber(draft.endDate);
  if (!draft.startTime && !draft.endTime) {
    return last < first ? null : { kind: 'days', days: last - first + 1 };
  }
  const start = first * 1440 + (draft.startTime ? minuteOf(draft.startTime) : 0);
  const end = last * 1440 + (draft.endTime ? minuteOf(draft.endTime) : 1440);
  return end > start ? { kind: 'minutes', minutes: end - start } : null;
}
